import type { HelpStep } from "newui/components/ui/Help";
import { TOUR_USER_INVITED } from "./tourFixtures";

/**
 * Guided tour steps for "Manage a user's roles" (PRO-730 topic chooser).
 *
 * Flow:
 *   1. Highlight the dummy row's "Manage roles" action → open the role
 *      drawer for it (the drawer only ever assigns/removes company-wide
 *      roles; see `AssignRoleDrawer`).
 *   2. The drawer as a whole.
 *   3. "Current roles" and the per-row remove control.
 *   4. "Add role (company-wide)" — the actual "adding a role" step.
 *   5. The bridge into scoping a role to projects/RFx events.
 *   6. Save → close the drawer.
 *
 * Like `userListSteps`, this never drives a real mutation: the drawer is
 * opened against the dummy `TOUR_USER_INVITED` shell (see
 * `UserListPage.openManageRolesForTour`), and `AssignRoleDrawer` itself
 * refuses to fire assign/remove mutations for a negative user id.
 */
export interface ManageRolesSteps {
	closeManageRoles: () => void;
	openManageRoles: () => void;
}

export const buildManageRolesSteps = ({
	openManageRoles,
	closeManageRoles,
}: ManageRolesSteps): HelpStep[] => [
	{
		target: `[data-testid="user-action-manage-roles-${TOUR_USER_INVITED.id}"]`,
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Manage a user&apos;s roles</h3>
				<p>
					Click the shield icon on any row to open that user&apos;s roles. We&apos;ll
					open it for you on the next step.
				</p>
			</div>
		),
		placement: "left",
		disableBeacon: true,
		onAfterStep: () => {
			openManageRoles();
		},
	},
	{
		target: '[data-testid="assign-role-drawer"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">The roles drawer</h3>
				<p>Assign or remove company-wide roles for this user here.</p>
			</div>
		),
		placement: "left",
		disableBeacon: true,
		spotlightPadding: 12,
	},
	{
		target: '[data-testid="drawer-role-list"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Current roles</h3>
				<p>
					Every role currently assigned is listed here. Click the trash icon next
					to a role to remove it.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
	},
	{
		target: '[data-testid="drawer-role-select"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Add a role</h3>
				<p>
					Pick a company-wide role from this list and it&apos;s assigned
					immediately once you save.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
	},
	{
		target: '[data-testid="drawer-scope-to-projects"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Need a narrower role?</h3>
				<p>
					Roles added here apply company-wide. Use this link to scope a role to
					specific projects or RFx events instead.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
	},
	{
		target: '[data-testid="drawer-save"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Save your changes</h3>
				<p>Click Save to apply the role you&apos;ve picked.</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
		onAfterStep: () => {
			closeManageRoles();
		},
	},
];
